import { useEffect, type ComponentProps } from 'react';
import { splitIds } from '@/clientOnlyUtils/clientUtils';

import {
  ContactDetailPageNotFound,
  ContactDetailPageSkeleton,
} from '@poseidon/components/ContactDetail';

import type { UserTeamData } from './ContactDetailPageNextJs';
import { useAuthContext } from '~/auth/auth.context';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { ContactDetailPageNextJs } from './ContactDetailPageNextJs';

type ContactDetailPageProps = {
  id: string | undefined;
  userTeam: UserTeamData;
  orgFirst?: boolean;
  authTeamId: string;
  authUserId: string;
  authIsSuperAdmin: boolean;
  onClose?: () => void;
} & ComponentProps<'div'>;

export const ContactDetailPage = ({ id, authTeamId, authUserId, authIsSuperAdmin, userTeam, onClose, orgFirst }: ContactDetailPageProps) => {
  const [query] = useRouterQuery();
  const [authData] = useAuthContext();
  const utils = api.useUtils();
  const { contactId, cadenceId } = splitIds(id ?? '');

  const contactQuery = api.contact.byId.useQuery(
    { contactId: contactId || '', cadenceId, userId: query.userId, teamId: authTeamId },
    { enabled: Boolean(contactId) },
  );

  // Break the contact-detail request waterfall: warm the Stage-2 caches with
  // the URL public id, in parallel with contact.byId, instead of waiting for it
  // to resolve before ContactDetailPageNextJs mounts and fires these. For
  // non-merged contacts these keys match the child's useQuery inputs exactly
  // (contactV2.id === the URL public id), so the child attaches to the warm /
  // in-flight cache. For merged contacts the resolved id differs and these
  // prefetches are simply discarded (fire-and-forget; prefetch swallows errors).
  const includeSignals = authIsSuperAdmin || authData.impersonation != null;
  useEffect(() => {
    if (!contactId) return;
    void utils.contactDetailBootstrap.forContact.prefetch({ teamId: authTeamId, contactId });
    void utils.contactDetailBootstrap.secondaryForContact.prefetch({ contactId, teamId: authTeamId, includeSignals });
    void utils.aiStep.actionSectionData.prefetch({ contactId, teamId: authTeamId, userId: authUserId });
    void utils.history.contact.prefetch({ contactId, filter: { invert: true } });
  }, [contactId, authTeamId, authUserId, includeSignals, utils]);

  // Loading: full-page skeleton that mirrors the real layout so the page
  // doesn't shift when data lands.
  if (contactQuery.isPending) {
    return <ContactDetailPageSkeleton />;
  }

  // Errored OR resolved-with-null: dedicated empty-state component with a
  // retry button. Covers tRPC errors and the "contact deleted / no access"
  // case in one branch.
  if (contactQuery.isError || contactQuery.data == null) {
    return <ContactDetailPageNotFound onRetry={() => contactQuery.refetch()} />;
  }

  return (
    <ContactDetailPageNextJs
      {...userTeam}
      authTeamId={authTeamId}
      authUserId={authUserId}
      propAuthIsSuperadmin={authIsSuperAdmin}
      data={contactQuery.data}
      isLoading={false}
      detailType={query.detailType}
      onClose={onClose}
      orgFirst={orgFirst}
    />
  );
};
